import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { mount, unmount } from "@redriver/aide";
import { getAuthClaims, refreshJwt } from "@redriver/cinnamon";
import usePermission from "components/auth/usePermission";
import { Actions, Targets } from "constants/permissions";
import { getSuggestedPrompts } from "./suggestedPrompts";

const BRAND_LOGO_SVG =
  '<svg viewBox="0 0 100 100" fill="currentColor" aria-hidden="true"><path d="M50 6 C 52 32, 68 48, 94 50 C 68 52, 52 68, 50 94 C 48 68, 32 52, 6 50 C 32 48, 48 32, 50 6 Z"/></svg>';

const AideAssistant = () => {
  // Stable for the app's lifetime in react-router v5, so the mount-once closure below is safe
  const history = useHistory();
  const dispatch = useDispatch();
  const { userId, userName, firstName, permissions } = useSelector(
    getAuthClaims
  );
  const developerToolsEnabled = usePermission(
    Targets.AssistantDeveloper,
    Actions.Access
  );

  useEffect(() => {
    const base = process.env.API_URL || "";
    const handle = mount({
      signalrUrl: `${base}signalr/aide`,
      apiBase: base,
      // Called on connect and every reconnect — resolves the CURRENT token
      accessTokenFactory: () => dispatch(refreshJwt()),
      user: {
        id: userId,
        displayName: userName,
        permissions: Object.keys(permissions || {}),
      },
      brandName: "Maples Teesdale AI",
      brandLogoSvg: BRAND_LOGO_SVG,
      // $primary-green — matches the global search button (styles/variables.scss)
      accentColour: "#90983d",
      welcomeTitle: firstName
        ? `Hi ${firstName}, how can I help?`
        : "How can I help?",
      welcomeText:
        "Type a question below to get started — I can help with document activity, downloads and user engagement across your sites.",
      suggestedPrompts: getSuggestedPrompts,
      pageUrlProvider: () => window.location.pathname,
      // Without this the widget falls back to a full reload that re-bootstraps the portal
      onNavigate: (path) => history.push(path),
      developerToolsEnabled,
      // Widget defaults mirror the backend's (aide.fileUpload in base.json) — override both
      // together or the server rejects what the composer accepts
      fileUpload: {},
    });
    // Lets the portal keep the bottom-right corner clear of the fixed chat launcher
    document.body.classList.add("aide-widget-mounted");
    // SPA navigation is invisible to the widget, so re-evaluate the per-page prompt chips on it
    const unlisten = history.listen(() => handle.notifyNavigation());
    return () => {
      unlisten();
      unmount();
      document.body.classList.remove("aide-widget-mounted");
    };
    // mount-once singleton: config changes after first render are deliberately not re-applied
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default AideAssistant;
